import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import './IssueParts.css';

const defaultForm = {
  partId: '',
  quantity: '',
  issueDate: '',
  referenceNo: '',
  issuedTo: '',
  reason: '',
  notes: '',
};

const toNumberString = (value, fallback = '') => {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? String(parsed) : fallback;
};

const isEmptyMoneyValue = (value) => (
  value === null
  || value === undefined
  || value === ''
  || Number(value) === 0
);

const getResolvedInventoryValues = (part, currentItem = {}) => {
  const resolvedCost = part?.unit_price ?? currentItem.cost ?? part?.opening_value ?? 0;
  const resolvedPrice = part?.Sale_Price ?? currentItem.price ?? part?.unit_price ?? part?.opening_value ?? 0;

  return {
    cost: toNumberString(resolvedCost, '0'),
    price: toNumberString(resolvedPrice, '0'),
  };
};

function IssueParts({ onLogout }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestIdFromUrl = searchParams.get('requestId');
  const [parts, setParts] = useState([]);
  const [filteredPartsMap, setFilteredPartsMap] = useState({}); // Map of row index -> filtered parts
  const [issueRequest, setIssueRequest] = useState(null);
  const [requestItems, setRequestItems] = useState([]);
  const [selectedPart, setSelectedPart] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [alternativeSearchTerm, setAlternativeSearchTerm] = useState('');
  const [alternativeResults, setAlternativeResults] = useState([]);
  const [alternativeMessage, setAlternativeMessage] = useState('');
  const [form, setForm] = useState(defaultForm);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const isReturnPendingRequest = issueRequest?.Status === 'Return Pending';

  const authHeaders = useCallback(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  }), []);

  // Fetch all parts (for non-issue-request mode)
  const fetchParts = useCallback(async () => {
    try {
      const response = await axios.get('/api/parts', {
        ...authHeaders(),
        params: { page: 1, pageSize: 50 },
      });
      setParts(response.data.parts || []);
    } catch (error) {
      setMessage(error.response?.data?.message || 'Failed to load parts.');
    }
  }, [authHeaders]);

  // Fetch filtered parts based on requested part number
  const fetchFilteredParts = useCallback(async (requestedPartNo) => {
    try {
      const response = await axios.get('/api/parts/filtered-for-issue', {
        ...authHeaders(),
        params: { requestedPartNo },
      });
      return response.data.parts || [];
    } catch (error) {
      console.error('Failed to fetch filtered parts:', error);
      return [];
    }
  }, [authHeaders]);

  const fetchIssueRequest = useCallback(async (requestId) => {
    if (!requestId) {
      setIssueRequest(null);
      setRequestItems([]);
      return;
    }

    try {
      const response = await axios.get(`/api/parts/issue-requests/${requestId}`, authHeaders());
      setIssueRequest(response.data.request);
      setRequestItems((response.data.items || []).map((item) => ({
        ...item,
        partId: item.PartId ? String(item.PartId) : '',
        deliveredQty: item.DeliveredQty || item.RequestedQty || '',
        price: toNumberString(item.Price, ''),
        cost: toNumberString(item.Cost, ''),
        unit: item.Unit || '',
        description: item.Description || '',
      })));
    } catch (error) {
      setMessage(error.response?.data?.message || 'Failed to load job card part issue request.');
    }
  }, [authHeaders]);

  const findRequestInventoryPart = useCallback((index, partId) => {
    const filteredList = filteredPartsMap[index] || [];
    return filteredList.find((item) => String(item.PartId) === String(partId))
      || parts.find((item) => String(item.PartId) === String(partId));
  }, [filteredPartsMap, parts]);

  useEffect(() => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    setForm((current) => ({ ...current, issueDate: now.toISOString().slice(0, 16) }));
    fetchParts();
    fetchIssueRequest(requestIdFromUrl);
  }, [fetchIssueRequest, fetchParts, requestIdFromUrl]);

  // Fetch filtered parts for each request item when issue request is loaded
  useEffect(() => {
    if (issueRequest?.Status === 'Pending' && requestItems.length > 0) {
      const fetchFilteredPartsForItems = async () => {
        const newFilteredPartsMap = {};
        
        for (let i = 0; i < requestItems.length; i++) {
          const item = requestItems[i];
          if (item.PartNumber) {
            console.log('Fetching filtered parts for item:', item.PartNumber);
            const filteredParts = await fetchFilteredParts(item.PartNumber);
            console.log('Filtered parts for', item.PartNumber, ':', filteredParts);
            newFilteredPartsMap[i] = filteredParts;
          }
        }
        
        setFilteredPartsMap(newFilteredPartsMap);
      };
      
      fetchFilteredPartsForItems();
    }
  }, [issueRequest, requestItems, fetchFilteredParts]);

  useEffect(() => {
    setRequestItems((current) => {
      let hasChanges = false;

      const nextItems = current.map((item, index) => {
        if (!item.partId) {
          return item;
        }

        const inventoryPart = findRequestInventoryPart(index, item.partId);
        if (!inventoryPart) {
          return item;
        }

        const resolvedValues = getResolvedInventoryValues(inventoryPart, item);
        const nextCost = isEmptyMoneyValue(item.cost) ? resolvedValues.cost : item.cost;
        const nextPrice = isEmptyMoneyValue(item.price) ? resolvedValues.price : item.price;
        const nextUnit = item.unit || inventoryPart.unit || '';
        const nextDescription = item.description || item.Description || inventoryPart.parts_name || '';
        const nextPartNumber = item.PartNumber || inventoryPart.Part_no || '';

        if (
          nextCost === item.cost
          && nextPrice === item.price
          && nextUnit === item.unit
          && nextDescription === item.description
          && nextPartNumber === item.PartNumber
        ) {
          return item;
        }

        hasChanges = true;
        return {
          ...item,
          cost: nextCost,
          price: nextPrice,
          unit: nextUnit,
          description: nextDescription,
          PartNumber: nextPartNumber,
        };
      });

      return hasChanges ? nextItems : current;
    });
  }, [findRequestInventoryPart]);

  const filteredParts = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) {
      return [];
    }

    return parts.filter((part) => (
      part.Part_no?.toLowerCase().includes(query)
      || part.parts_name?.toLowerCase().includes(query)
      || part.Brand?.toLowerCase().includes(query)
      || part.Location?.toLowerCase().includes(query)
    )).slice(0, 8);
  }, [parts, searchTerm]);

  const selectPart = (part) => {
    setSelectedPart(part);
    setForm((current) => ({ ...current, partId: part.PartId }));
    setSearchTerm(`${part.Part_no || ''} ${part.parts_name || ''}`.trim());
    setMessage('');
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === 'quantity' && value !== '' && Number(value) < 0) {
      setForm((current) => ({ ...current, [name]: '0' }));
      return;
    }

    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage('');

    if (!selectedPart) {
      setMessage('Please select a part first.');
      return;
    }

    const quantity = parseFloat(form.quantity);
    const availableQty = parseFloat(selectedPart.Qty ?? selectedPart.opening_qty ?? 0);

    if (Number.isNaN(quantity) || quantity <= 0) {
      setMessage('Quantity must be greater than zero.');
      return;
    }

    if (quantity > availableQty) {
      setMessage(`Lower Qty in Stock. Available Qty: ${availableQty} ${selectedPart.unit || ''}`);
      return;
    }

    setLoading(true);

    try {
      await axios.post('/api/parts/issue', {
        ...form,
        partId: selectedPart.PartId,
        quantity,
      }, authHeaders());

      setMessage('Part issued successfully.');
      setSelectedPart(null);
      setSearchTerm('');
      setForm((current) => ({
        ...defaultForm,
        issueDate: current.issueDate,
      }));
      await fetchParts();
    } catch (error) {
      setMessage(error.response?.data?.message || 'Failed to issue part.');
    } finally {
      setLoading(false);
    }
  };

  const updateRequestItem = (index, field, value) => {
    setRequestItems((current) => current.map((item, itemIndex) => (itemIndex === index ? { ...item, [field]: value } : item)));

    if (field === 'deliveredQty') {
      const currentItem = requestItems[index];
      if (currentItem?.partId) {
        validateRequestItemStock(index, { ...currentItem, deliveredQty: value });
      }
    }
  };

  const getStockQty = (part) => parseFloat(part?.Qty ?? part?.opening_qty ?? 0);

  const validateRequestItemStock = (index, item) => {
    const part = findRequestInventoryPart(index, item.partId);
    if (!part) {
      return true;
    }

    const requiredQty = parseFloat(item.deliveredQty || item.RequestedQty || 0);
    const availableQty = getStockQty(part);

    if (!Number.isNaN(requiredQty) && requiredQty > availableQty) {
      setMessage(`Lower Qty in Stock. Available Qty: ${availableQty} ${part.unit || item.unit || ''}`);
      return false;
    }

    setMessage('');
    return true;
  };

  const selectRequestPart = (index, partId) => {
    // First check filtered parts, then fall back to all parts
    const part = findRequestInventoryPart(index, partId);
    const currentItem = requestItems[index];
    const resolvedValues = getResolvedInventoryValues(part, currentItem);
    const nextItem = {
      ...currentItem,
      partId,
      PartNumber: part?.Part_no || currentItem.PartNumber,
      description: part?.parts_name || currentItem.description,
      unit: part?.unit || currentItem.unit,
      cost: resolvedValues.cost,
      price: resolvedValues.price,
    };

    validateRequestItemStock(index, nextItem);
    
    setRequestItems((current) => current.map((item, itemIndex) => {
      if (itemIndex !== index) {
        return item;
      }

      return nextItem;
    }));
  };

  const deliverIssueRequest = async () => {
    if (!issueRequest) {
      return;
    }

    const deliveryItems = requestItems.map((item) => ({
      partIssueRequestItemId: item.PartIssueRequestItemId,
      partId: item.partId,
      partNumber: item.PartNumber,
      description: item.description,
      deliveredQty: parseFloat(item.deliveredQty),
      unit: item.unit,
      cost: parseFloat(item.cost || 0),
      price: parseFloat(item.price || 0),
    }));

    if (deliveryItems.some((item) => !item.partId || Number.isNaN(item.deliveredQty) || item.deliveredQty <= 0 || Number.isNaN(item.price))) {
      setMessage('Select inventory part, quantity, and price for every request row.');
      return;
    }

    const lowerStockItem = requestItems.find((item, index) => {
      const part = findRequestInventoryPart(index, item.partId);
      const requiredQty = parseFloat(item.deliveredQty || item.RequestedQty || 0);
      return part && !Number.isNaN(requiredQty) && requiredQty > getStockQty(part);
    });

    if (lowerStockItem) {
      const lowerStockIndex = requestItems.indexOf(lowerStockItem);
      const part = findRequestInventoryPart(lowerStockIndex, lowerStockItem.partId);
      setMessage(`Lower Qty in Stock. Available Qty: ${getStockQty(part)} ${part?.unit || lowerStockItem.unit || ''}`);
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      await axios.post(`/api/parts/issue-requests/${issueRequest.PartIssueRequestId}/deliver`, { items: deliveryItems }, authHeaders());
      setMessage('Parts delivered to job card. Waiting for job card receive.');
      await Promise.all([fetchParts(), fetchIssueRequest(issueRequest.PartIssueRequestId)]);
      try {
        // Dismiss the activity notification so it no longer appears in activity list
        await axios.post(`/api/jobcards/part-issue-activities/${issueRequest.PartIssueRequestId}/dismiss`, {}, authHeaders());
      } catch (dismissErr) {
        // non-fatal: keep UI state updated even if dismiss fails
        console.warn('Failed to dismiss part-issue activity after delivery', dismissErr);
      }
    } catch (error) {
      setMessage(error.response?.data?.message || 'Failed to deliver parts to job card.');
    } finally {
      setLoading(false);
    }
  };

  const acceptReturnRequest = async () => {
    if (!issueRequest) {
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      await axios.post(`/api/parts/issue-requests/${issueRequest.PartIssueRequestId}/accept-return`, {}, authHeaders());
      setMessage('Return accepted and stock updated.');
      await Promise.all([fetchParts(), fetchIssueRequest(issueRequest.PartIssueRequestId)]);
      try {
        // Dismiss the activity notification after accepting returned part
        await axios.post(`/api/jobcards/part-issue-activities/${issueRequest.PartIssueRequestId}/dismiss`, {}, authHeaders());
      } catch (dismissErr) {
        console.warn('Failed to dismiss part-issue activity after accepting return', dismissErr);
      }
    } catch (error) {
      setMessage(error.response?.data?.message || 'Failed to accept rejected part return.');
    } finally {
      setLoading(false);
    }
  };

  const searchAlternativeParts = async () => {
    const partNo = alternativeSearchTerm.trim();
    setAlternativeMessage('');
    setAlternativeResults([]);

    if (!partNo) {
      setAlternativeMessage('Write part no first.');
      return;
    }

    try {
      const response = await axios.get('/api/parts/alternative/search', {
        ...authHeaders(),
        params: { partNo },
      });
      setAlternativeResults(response.data.alternatives || []);
      if ((response.data.alternatives || []).length === 0) {
        setAlternativeMessage('No alternative parts found.');
      }
    } catch (error) {
      setAlternativeMessage(error.response?.data?.message || 'Failed to search alternative parts.');
    }
  };

  return (
    <div className="home-container">
      <Sidebar onLogout={onLogout} />
      <main className="issue-parts-page">
        <header className="issue-parts-header">
          <button className="app-back-button" type="button" onClick={() => navigate('/inventory')}>
            Back
          </button>
          <h1>Issue Parts</h1>
        </header>

        <section className="issue-parts-layout">
          {issueRequest && (
            <section className="issue-parts-form jobcard-issue-request">
              <div className="issue-request-title">
                <div>
                  <h2>Job Card #{issueRequest.JobNumber || issueRequest.JobCardId}</h2>
                  <p>
                    {issueRequest.ClientName || 'No client'}
                    {issueRequest.CarNumber ? ` | ${issueRequest.CarNumber}` : ''}
                  </p>
                </div>
                <span>{issueRequest.Status}</span>
              </div>

              <table className="issue-request-table">
                <thead>
                  <tr>
                    <th>Requested Part</th>
                    <th>{isReturnPendingRequest ? 'Inventory Part' : 'Inventory Part'}</th>
                    <th>Qty</th>
                    <th>Cost</th>
                    <th>Price</th>
                    <th>Unit</th>
                  </tr>
                </thead>
                <tbody>
                  {requestItems.map((item, index) => (
                    <tr key={item.PartIssueRequestItemId}>
                      <td>
                        <strong>{item.PartNumber}</strong>
                        <small>{item.Description || item.description || '-'}</small>
                      </td>
                      <td>
                        <select
                          value={item.partId}
                          onChange={(event) => selectRequestPart(index, event.target.value)}
                          disabled={issueRequest.Status !== 'Pending'}
                        >
                          <option value="">{isReturnPendingRequest ? 'Returned part' : 'Select part'}</option>
                          {(filteredPartsMap[index] !== undefined
                            ? filteredPartsMap[index]
                            : parts
                          ).map((part) => (
                            <option key={part.PartId} value={part.PartId}>
                              {part.Part_no || part.parts_name} - {part.parts_name} ({part.Qty ?? part.opening_qty ?? 0})
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.deliveredQty}
                          onChange={(event) => updateRequestItem(index, 'deliveredQty', event.target.value)}
                          disabled={issueRequest.Status !== 'Pending'}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.cost}
                          onChange={(event) => updateRequestItem(index, 'cost', event.target.value)}
                          disabled={issueRequest.Status !== 'Pending'}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.price}
                          onChange={(event) => updateRequestItem(index, 'price', event.target.value)}
                          disabled={issueRequest.Status !== 'Pending'}
                        />
                      </td>
                      <td>
                        <input
                          value={item.unit}
                          onChange={(event) => updateRequestItem(index, 'unit', event.target.value)}
                          disabled={issueRequest.Status !== 'Pending'}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {message && <div className="issue-parts-message">{message}</div>}

              <div className="issue-actions">
                {issueRequest.Status === 'Pending' && (
                  <button type="button" onClick={deliverIssueRequest} disabled={loading}>
                    {loading ? 'Delivering...' : 'Delivery'}
                  </button>
                )}
                {issueRequest.Status === 'Return Pending' && (
                  <button type="button" onClick={acceptReturnRequest} disabled={loading}>
                    {loading ? 'Accepting...' : 'Accept Return'}
                  </button>
                )}
                <button type="button" onClick={() => navigate(`/jobcards/${issueRequest.JobCardId}/edit`)}>
                  Open Job Card
                </button>
              </div>
            </section>
          )}

          {!issueRequest && (
          <form className="issue-parts-form" onSubmit={handleSubmit}>
            <label className="issue-parts-search">
              Part Search
              <input
                type="text"
                placeholder="Search by part number, name, brand, or location"
                value={searchTerm}
                onChange={(event) => {
                  setSearchTerm(event.target.value);
                  setSelectedPart(null);
                  setForm((current) => ({ ...current, partId: '' }));
                }}
              />
              {filteredParts.length > 0 && (
                <div className="issue-parts-suggestions">
                  {filteredParts.map((part) => (
                    <button key={part.PartId} type="button" onClick={() => selectPart(part)}>
                      <strong>{part.Part_no || '-'}</strong>
                      <span>{part.parts_name}</span>
                      <em>{part.Qty ?? part.opening_qty ?? 0} {part.unit || ''}</em>
                    </button>
                  ))}
                </div>
              )}
            </label>

            {selectedPart && (
              <div className="issue-selected-part">
                <strong>{selectedPart.parts_name}</strong>
                <span>Part No: {selectedPart.Part_no || '-'}</span>
                <span>Stock: {selectedPart.Qty ?? selectedPart.opening_qty ?? 0} {selectedPart.unit || ''}</span>
                <span>Location: {selectedPart.Location || '-'}</span>
              </div>
            )}

            <div className="issue-field-grid">
              <label>
                Quantity
                <input
                  name="quantity"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.quantity}
                  onChange={handleChange}
                  required
                />
              </label>

              <label>
                Issue Date
                <input
                  name="issueDate"
                  type="datetime-local"
                  value={form.issueDate}
                  onChange={handleChange}
                />
              </label>

              <label>
                Reference No
                <input
                  name="referenceNo"
                  value={form.referenceNo}
                  onChange={handleChange}
                  placeholder="Job card, invoice, or note"
                />
              </label>

              <label>
                Issued To
                <input
                  name="issuedTo"
                  value={form.issuedTo}
                  onChange={handleChange}
                  placeholder="Technician, customer, or vehicle"
                />
              </label>
            </div>

            <label>
              Reason
              <input
                name="reason"
                value={form.reason}
                onChange={handleChange}
                placeholder="Repair, internal use, warranty, etc."
              />
            </label>

            <label>
              Notes
              <textarea
                name="notes"
                value={form.notes}
                onChange={handleChange}
                placeholder="Optional notes"
              />
            </label>

            {message && <div className="issue-parts-message">{message}</div>}

            <div className="issue-actions">
              <button type="submit" disabled={loading}>
                {loading ? 'Issuing...' : 'Issue Part'}
              </button>
              <button type="button" onClick={() => navigate('/inventory')}>
                Cancel
              </button>
            </div>
          </form>
          )}

          <section className="issue-history alternative-search-panel">
            <h2>Search Alternative Part</h2>
            <div className="alternative-search-row">
              <input
                type="text"
                value={alternativeSearchTerm}
                onChange={(event) => setAlternativeSearchTerm(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    searchAlternativeParts();
                  }
                }}
                placeholder="Write part no"
              />
              <button type="button" onClick={searchAlternativeParts}>
                Search
              </button>
            </div>

            {alternativeMessage && <div className="alternative-message">{alternativeMessage}</div>}

            {alternativeResults.length > 0 && (
              <ol className="alternative-results">
                {alternativeResults.slice(0, 10).map((partNo, index) => (
                  <li key={`${partNo}-${index}`}>{partNo}</li>
                ))}
              </ol>
            )}
          </section>
        </section>
      </main>
    </div>
  );
}

export default IssueParts;
